import { useEffect, useState } from "react";
import { useChat, Message } from "ai/react";
import { useWorkspace } from "../context/wrokspace";
import { IdToken, useAuth0 } from "@auth0/auth0-react";
import { useInterval } from "@/hooks/useInterval";
import { Instruction } from "../types";
import { params } from "@/params";
import { useAuthorizedMutation } from "@/api";
import { useReleases } from "../context/release";
import { Skill } from "@/v2/entities/skill/Skill.model";
import { useHitlSlackChannelId } from "@/v2/shared/hooks/useHitlSlackChannelId/useHitlSlackChannelId";

export const useSimulatorChat = ({
  id,
  isTraining,
  instructions,
  topic,
  topicId,
  subTopic,
  handleResponse,
  briefs = "",
  skills,
  handleError,
}: {
  id: string;
  isTraining: boolean;
  instructions: Instruction[];
  topic: string;
  topicId: string | null;
  subTopic: string;
  handleResponse: (message: Message) => void;
  briefs?: string;
  skills: Skill[];
  handleError: (e?: any) => void;
}) => {
  const { simulator, relevantRelease } = useReleases();
  const { workspace } = useWorkspace();
  const { user } = useAuth0();
  const { channelId: hitlSlackChannelId } = useHitlSlackChannelId();
  const [tokenId, setToken] = useState<IdToken | undefined>(undefined);
  const { isAuthenticated, getIdTokenClaims } = useAuth0();

  useEffect(() => {
    (async () => {
      const token = await getIdTokenClaims();
      setToken(token);
    })();
  }, [isAuthenticated, getIdTokenClaims]);

  useInterval(() => {
    (async () => {
      if (isAuthenticated) {
        const token = await getIdTokenClaims();
        setToken(token);
      }
    })();
  }, 1000 * 60 * 10); // every 10 minutes

  const authToken = tokenId?.__raw ? tokenId.__raw : "";
  const headers = {
    Authorization: `Bearer ${authToken}`,
  };

  const isChat = simulator?.channel === "autonomous_quackchat";

  const getSubdomain = () => {
    const hostname = window.location.hostname;
    const subdomain = hostname.split(".")[0];
    return subdomain;
  };

  const subdomain = getSubdomain();

  const {
    messages,
    input,
    handleInputChange,
    handleSubmit,
    setInput,
    append,
    reload,
    setMessages,
    isLoading,
  } = useChat({
    id: String(id),
    api: isChat
      ? `${params.API_URL}/platform/vercel-proxy/api/reply-chat/train?stream=true&playground=${isTraining}&simulator=true`
      : `${params.API_URL}/platform/vercel-proxy/api/reply?stream=true&playground=${isTraining}&simulator=true`,
    // api: `http://localhost:3002/api/reply-chat/train?stream=true&playground=${isTraining}&simulator=true`,
    headers,
    body: {
      tenant: workspace || user?.owner || subdomain || "",
      instructions,
      topic,
      subTopic,
      brief: briefs,
      skills,
      simulatorId: simulator?.id,
      releaseId: relevantRelease?.id ?? 0,
      topicId,
      seperateGoodFeedback: simulator?.seperateGoodFeedback,
      customToneOfVoice: relevantRelease?.customToneOfVoice,
      slackChannelId: hitlSlackChannelId,
    },
    onFinish: handleResponse,
    onError: handleError,
  });

  const handleSubmitWithDelay = (e: React.FormEvent<HTMLFormElement>) => {
    handleSubmit(e);
  };

  return {
    messages,
    input,
    handleInputChange,
    handleSubmit: handleSubmitWithDelay,
    append,
    reload,
    setMessages,
    isLoading,
    setInput,
  };
};

export const useBadFeedback = ({
  isTraining,
  isChat,
}: {
  isTraining: boolean;
  isChat: boolean;
}) => {
  return useAuthorizedMutation({
    url: isChat
      ? `${params.API_URL}/platform/vercel-proxy/api/reply-chat?stream=false&playground=${isTraining}&simulator=true`
      : `${params.API_URL}/platform/vercel-proxy/api/reply?stream=false&playground=${isTraining}&simulator=true`,
    method: "POST",
  });
};

export const usePositiveFeedback = () => {
  return useAuthorizedMutation({
    url: `${params.API_URL}/platform/vercel-proxy/api/rag`,
    method: "PUT",
  });
};
export const useForgetFeedback = () => {
  return useAuthorizedMutation({
    url: `${params.API_URL}/platform/vercel-proxy/api/rag/delete`,
    method: "POST",
  });
};

export const useFetchTopics = () => {
  return useAuthorizedMutation({
    url: `${params.API_URL}/platform/vercel-proxy/api/topics`,
    method: "POST",
  });
};

export const useHandleInstructify = () => {
  return useAuthorizedMutation({
    url: `${params.API_URL}/platform/vercel-proxy/api/instructify`,
    method: "POST",
  });
};
